import { useMutation } from "@tanstack/react-query";
import { type Api, updateWorkoutItemGroup } from "api-client";
import {
  type WorkoutFormat,
  type WorkoutItemGroupType,
} from "api-client/legacy-trpc";
import { type TFunction } from "i18next";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import { type GroupWorkoutItemData } from "src/workouts/WorkoutEditStore";

import { AmrapSectionFields } from "./AmrapSectionFields";
import { FreestyleSectionFields } from "./FreestyleSectionFields";
import { RegularSectionFields } from "./RegularSectionFields";
import { TimedSectionFields } from "./TimedSectionFields";

export interface SectionInlineFieldsProps {
  workoutItemId: string;
}

export const SectionInlineFields = observer(function SectionInlineFields(
  props: SectionInlineFieldsProps,
) {
  const workoutEditStore = useWorkoutEditStore();
  const { startSaving } = useAuthenticatedNavigationContext();
  const workoutItem = workoutEditStore.getWorkoutItemById(
    props.workoutItemId,
  ) as GroupWorkoutItemData;

  const format = workoutItem.format;
  const groupType = workoutItem.groupType;

  const updateMutation = useMutation({
    mutationFn: async (body: Api.UpdateWorkoutItemGroup.Input["body"]) => {
      return await updateWorkoutItemGroup({
        path: {
          workoutId: workoutEditStore.workout.id,
          workoutItemId: props.workoutItemId,
        },
        body,
      });
    },
  });

  const handleUpdate = useCallback(
    async (updates: Api.UpdateWorkoutItemGroup.Input["body"]) => {
      const stopSaving = startSaving();
      workoutEditStore.updateWorkoutItem(props.workoutItemId, updates);

      try {
        await updateMutation.mutateAsync(updates);
        stopSaving();
      } catch {
        stopSaving("error");
      }
    },
    [startSaving, workoutEditStore, props.workoutItemId, updateMutation],
  );

  if (groupType === "SUPER_SET") {
    return null;
  }

  switch (format) {
    case "FREESTYLE":
      return (
        <FreestyleSectionFields
          workoutItem={workoutItem}
          onUpdate={handleUpdate}
        />
      );
    case "AMRAP":
      return (
        <AmrapSectionFields workoutItem={workoutItem} onUpdate={handleUpdate} />
      );
    case "TIMED":
      return (
        <TimedSectionFields workoutItem={workoutItem} onUpdate={handleUpdate} />
      );
    case "REGULAR":
    default:
      return (
        <RegularSectionFields
          workoutItem={workoutItem}
          onUpdate={handleUpdate}
        />
      );
  }
});

export function getSectionFormatLabel(
  format: WorkoutFormat | null,
  t: TFunction,
): string {
  switch (format) {
    case "REGULAR":
      return t("editWorkout.sectionFormat.regular");
    case "AMRAP":
      return t("editWorkout.sectionFormat.amrap");
    case "TIMED":
      return t("editWorkout.sectionFormat.timed");
    case "FREESTYLE":
      return t("editWorkout.sectionFormat.freestyle");
    default:
      return "";
  }
}

export function getSectionTypeLabel(
  groupType: WorkoutItemGroupType | null,
  t: TFunction,
): string {
  switch (groupType) {
    case "WARM_UP":
      return t("editWorkout.sectionType.warmup");
    case "WORKOUT":
      return t("editWorkout.sectionType.workout");
    case "COOLDOWN":
      return t("editWorkout.sectionType.cooldown");
    case "RECOVERY":
      return t("editWorkout.sectionType.recovery");
    case "SUPER_SET":
      return t("editWorkout.sectionFormat.superSet");
    default:
      return "";
  }
}
